import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';
/** How a task opens from a view: a centered popup or a side panel. */
export type TaskLayout = 'modal' | 'panel';

interface UiState {
  theme: Theme;
  sidebarCollapsed: boolean;
  /** Below the md breakpoint the sidebar is an off-canvas drawer; this is its open state (not persisted). */
  sidebarOpen: boolean;
  /**
   * Tree nodes (spaces and folders) the user has opened or closed. Missing means the default:
   * spaces start closed so a long client list stays scannable, folders start open.
   */
  collapsedNodes: Record<string, boolean>;
  lastWorkspaceId: string | null;
  /** Last opened view per location, keyed by `list:<id>`, `space:<id>`... */
  activeViews: Record<string, string>;
  commandOpen: boolean;
  shortcutsOpen: boolean;
  taskLayout: TaskLayout;
  setTheme(theme: Theme): void;
  setTaskLayout(layout: TaskLayout): void;
  toggleSidebar(): void;
  setSidebarOpen(open: boolean): void;
  toggleNode(id: string, defaultCollapsed?: boolean): void;
  setNodeCollapsed(id: string, collapsed: boolean): void;
  setNodesCollapsed(ids: string[], collapsed: boolean): void;
  setLastWorkspace(id: string | null): void;
  setActiveView(scopeKey: string, viewId: string): void;
  /** Forget every remembered view, so locations open on their default view again. */
  clearActiveViews(): void;
  setCommandOpen(open: boolean): void;
  setShortcutsOpen(open: boolean): void;
}

/** UI-only client state. Server data lives in TanStack Query. */
export const useUi = create<UiState>()(
  persist(
    (set) => ({
      theme: 'system',
      sidebarCollapsed: false,
      sidebarOpen: false,
      collapsedNodes: {},
      lastWorkspaceId: null,
      activeViews: {},
      commandOpen: false,
      shortcutsOpen: false,
      taskLayout: 'modal',
      setTheme: (theme) => set({ theme }),
      setTaskLayout: (taskLayout) => set({ taskLayout }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      toggleNode: (id, defaultCollapsed = false) =>
        set((s) => ({ collapsedNodes: { ...s.collapsedNodes, [id]: !(s.collapsedNodes[id] ?? defaultCollapsed) } })),
      setNodeCollapsed: (id, collapsed) =>
        set((s) => ({ collapsedNodes: { ...s.collapsedNodes, [id]: collapsed } })),
      setNodesCollapsed: (ids, collapsed) =>
        set((s) => ({ collapsedNodes: { ...s.collapsedNodes, ...Object.fromEntries(ids.map((id) => [id, collapsed])) } })),
      setLastWorkspace: (lastWorkspaceId) => set({ lastWorkspaceId }),
      setActiveView: (scopeKey, viewId) => set((s) => ({ activeViews: { ...s.activeViews, [scopeKey]: viewId } })),
      clearActiveViews: () => set({ activeViews: {} }),
      setCommandOpen: (commandOpen) => set({ commandOpen }),
      setShortcutsOpen: (shortcutsOpen) => set({ shortcutsOpen }),
    }),
    {
      name: 'regent.ui',
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        collapsedNodes: s.collapsedNodes,
        lastWorkspaceId: s.lastWorkspaceId,
        activeViews: s.activeViews,
        taskLayout: s.taskLayout,
      }),
    },
  ),
);

const THEME_KEY = 'regent.theme';

function systemDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme(theme: Theme): void {
  const dark = theme === 'dark' || (theme === 'system' && systemDark());
  document.documentElement.classList.toggle('dark', dark);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {}
}

/** Call once at startup: restores the saved theme and follows the OS when set to `system`. */
export function initTheme(): void {
  let saved: Theme = 'system';
  try {
    saved = (localStorage.getItem(THEME_KEY) as Theme | null) ?? 'system';
  } catch {}
  useUi.setState({ theme: saved });
  applyTheme(saved);
  useUi.subscribe((state, prev) => {
    if (state.theme !== prev.theme) applyTheme(state.theme);
  });
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (useUi.getState().theme === 'system') applyTheme('system');
  });
}

export function isDark(): boolean {
  return document.documentElement.classList.contains('dark');
}

export function toggleTheme(): void {
  useUi.getState().setTheme(isDark() ? 'light' : 'dark');
}
